import Color from "ui/helpers/Color";

const HSV_VALUES = [255, 242, 229, 211, 195, 170, 145, 120, 95, 70, 55, 30];

const HSV_SATURATION_DIVISIONS = 11;

/**
 * @typedef {object} T_PaletteModelNode
 * @property {number} x
 * @property {number} y
 * @property {number} z
 * @property {number} width
 * @property {number} height
 * @property {number} depth
 * @property {number} cx (x + width / 2)
 * @property {number} cy (y + height / 2)
 * @property {number} cz (z + depth / 2)
 * @property {?Color} color
 * @property {?Array.<T_PaletteModelNode>} nodes The following list:
 *  [
 *      0: <=cx <=cy <=cz (000),
 *      1: <=cx <=cy  >cz (001),
 *      2: <=cx  >cy <=cz (010),
 *      3: <=cx  >cy  >cz (011),
 *      4:  >cx <=cy <=cz (100),
 *      5:  >cx <=cy  >cz (101),
 *      6:  >cx  >cy <=cz (110),
 *      7:  >cx  >cy  >cz (111),
 *  ]
 */

/**
 * Colors palette model
 */
class PaletteModel {
	/**
	 * Class constructor
	 */
	constructor() {
		/**
		 * List of the colors
		 *
		 * @private
		 * @type {Array<Color>}
		 */
		this._colors = [];

		/**
		 * Conversion map
		 *
		 * @type {Object}
		 * @private
		 */
		this._convertMap = {};

		/**
		 * Root OctalTree node
		 *
		 * @type {?T_PaletteModelNode}
		 * @private
		 */
		this._rootNode = null;

		/**
		 * @type {Map<number, Color>}
		 * @private
		 */
		this._hue = (() => {
			let hue = new Map();
			let exclude = [130, 140, 250];
			for (let h = 0; h < 360; h += 10) {
				if (exclude.includes(h)) {
					continue;
				}
				hue.set(h, new Color().setHSV(h, 1, 1));
			}
			return hue;
		})();

		/**
		 * @type {Map<Color, number>}
		 * @private
		 */
		this._hueByColors = new Map();

		/**
		 * @type {Map<number, Array<Color>>}
		 * @private
		 */
		this._colorsByHue = (() => {
			const map = new Map();

			for (let h of this._hue.keys()) {
				const colors = [];
				for (let value of HSV_VALUES) {
					for (let s = 0; s < HSV_SATURATION_DIVISIONS; s++) {
						let color, key, newValue = value;
						if (s > 0) {
							let i = 100;
							do {
								color = new Color().setHSV(h, s / 10, newValue / 255);
								key = color.r + '.' + color.g + '.' + color.b;
								newValue = Math.min(255, newValue + 5);
							} while (this._convertMap[key] && --i > 0);
						} else {
							color = new Color().setHSV(h, s / 10, newValue / 255);
							key = color.r + '.' + color.g + '.' + color.b;
						}

						colors.push(color);
						this._colors.push(color);
						this._hueByColors.set(color, h);
						this._convertMap[key] = color;
					}
				}
				map.set(h, colors);
			}
			return map;
		})();

		this._buildIndex();
	}

	/**
	 * Returns list of the palette colors
	 *
	 * @return {Array<Color>}
	 */
	get colors() {
		return this._colors;
	}

	/**
	 * Returns list of the color by hue value
	 *
	 * @returns {Array<{hue: number, color: Color}>}
	 */
	get hueColors() {
		return [...this._hue.entries()].map(([hue, color]) => {return {hue, color}});
	}

	/**
	 * Returns most black color with lower saturation
	 *
	 * @return {Color}
	 */
	getBlackColor() {
		const i = (HSV_SATURATION_DIVISIONS - 1) * HSV_VALUES.length + 1;
		return this._colorsByHue.get(this._hue.keys().next().value)[i];
	}

	/**
	 * Returns list of the colors
	 *
	 * @param {number} hue
	 * @returns {Array<Color>}
	 */
	getColorsByHue(hue) {
		hue = Math.round(hue);

		for (let a = 0; a <= 2; a++) {
			if (this._colorsByHue.has(hue - a)) {
				return this._colorsByHue.get(hue - a);
			} else if (this._colorsByHue.has(hue + a)) {
				return this._colorsByHue.get(hue + a);
			}
		}

		return [];
	}

	/**
	 * Returns list of the colors
	 *
	 * @param {Color} color
	 * @returns {{hue: number, colors: Array<Color>}}
	 */
	getPaletteByColor(color) {
		const trueColor = this.convertColor(color.r, color.g, color.b);
		if (trueColor === null) {
			return {hue: 0, colors: this._colorsByHue.get(0)};
		}

		const hue = this._hueByColors.get(trueColor);

		return {hue, colors: this._colorsByHue.get(hue)};
	}

	/**
	 * Converts arbitrary color to the palette color
	 *
	 * @param {number} r
	 * @param {number} g
	 * @param {number} b
	 * @returns {Color}
	 */
	convertColor(r, g, b) {
		let map = this._convertMap,
			key = r + '.' + g + '.' + b,
			c = map[key];

		if (typeof c !== 'undefined') {
			return c;
		}

		let node = this._rootNode, cx, cy, cz, i;
		do {
			cx = node.cx;
			cy = node.cy;
			cz = node.cz;
			if (r <= cx) {
				if (g <= cy) {
					i = b <= cz ? 0 : 1;
				} else {
					i = b <= cz ? 2 : 3;
				}
			} else {
				if (g <= cy) {
					i = b <= cz ? 4 : 5;
				} else {
					i = b <= cz ? 6 : 7;
				}
			}
			node = node.nodes[i];
		} while (node && node.nodes);

		return map[key] = (node ? node.color : null);
	}

	/**
	 * Builds Octal Tree Index with all palette colors
	 *
	 * @private
	 * @returns {void}
	 */
	_buildIndex() {
		this._rootNode = this._createNode(
			this._colors.slice(),
			0, 0, 0, 256, 256, 256
		);
	}

	/**
	 *
	 * @param {Array<?Color>} colors
	 * @param {number} x
	 * @param {number} y
	 * @param {number} z
	 * @param {number} width
	 * @param {number} height
	 * @param {number} depth
	 * @returns {?T_PaletteModelNode}
	 * @private
	 */
	_createNode(colors, x, y, z, width, height, depth) {
		let color,
			x2 = x + width,
			y2 = y + height,
			z2 = z + depth,
			childColors = [];

		for (let i = 0, l = colors.length; i < l; i++) {
			color = colors[i];
			if (color === null) {
				continue;
			}
			let {r, g, b} = color;
			if (r >= x && r < x2 && g >= y && g < y2 && b >= z && b < z2) {
				colors[i] = null;
				childColors.push(color);
			}
		}

		let w2 = width >> 1,
			h2 = height >> 1,
			d2 = depth >> 1,
			cx = x + w2,
			cy = y + h2,
			cz = z + d2,
			node = {x, y, z, width, height, depth, cx, cy, cz, nodes: null, color: null};

		if (childColors.length === 0) {
			return node;
		}

		if (childColors.length === 1) {
			node.color = childColors[0];
		} else {
			/** @type {Array.<Color>} */
			let childColorsCopy = childColors.slice();
			node.nodes = [
				this._createNode(childColors,  x,  y,  z, w2, h2, d2),
				this._createNode(childColors,  x,  y, cz, w2, h2, d2),
				this._createNode(childColors,  x, cy,  z, w2, h2, d2),
				this._createNode(childColors,  x, cy, cz, w2, h2, d2),
				this._createNode(childColors, cx,  y,  z, w2, h2, d2),
				this._createNode(childColors, cx,  y, cz, w2, h2, d2),
				this._createNode(childColors, cx, cy,  z, w2, h2, d2),
				this._createNode(childColors, cx, cy, cz, w2, h2, d2),
			];
			for (let childNode of node.nodes) {
				if (childNode.color === null && childNode.nodes === null) {
					let minDist = Number.MAX_VALUE, neighborColor;
					for (let color of childColorsCopy) {
						let dx = color.r - childNode.cx,
							dy = color.g - childNode.cy,
							dz = color.b - childNode.cz,
							dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
						if (dist < minDist) {
							minDist = dist;
							neighborColor = color;
						}
					}
					childNode.color = neighborColor;
				}
			}
		}
		return node;
	}
}

export default PaletteModel;
